import { get_staff_summary, download_staff_pdf } from "services/api-service";
import { REACT_APP_API_URL, API_ROUTES } from "../../config/api-config";
import React, { useState, useEffect, useMemo } from "react";
// Add this helper component before your main StaffAttendanceTable component
function AsyncImage({ src, alt, style }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        width: style.width || "100%",
        maxWidth: style.maxWidth || "130px",
        height: style.height || "130px",
        display: "inline-block",
      }}
    >
      {/* 1. Loading Placeholder (Only shows if it hasn't loaded yet) */}
      {!hasLoaded && !error && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: style.borderRadius || "6px",
            backgroundColor: "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            fontSize: "11px",
            fontWeight: "500",
            border: "1px dashed #d1d5db",
            zIndex: 1,
          }}
        >
          ⏳ Loading...
        </div>
      )}

      {/* 2. Error Fallback */}
      {error && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: style.borderRadius || "6px",
            backgroundColor: "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ef4444",
            fontSize: "11px",
            fontWeight: "500",
            border: "1px solid #fca5a5",
            zIndex: 1,
          }}
        >
          ⚠️ Failed
        </div>
      )}

      {/* 3. Actual Image */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          ...style,
          opacity: hasLoaded ? 1 : 0, // Smooth fade-in once ready
          transition: "opacity 0.2s ease-in-out",
        }}
        onLoad={() => setHasLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}
// ============================================================
// WORKING SESSIONS COMPONENT - DISPLAYS ENTRY/EXIT PAIRINGS
// ============================================================
function WorkingSessionsDisplay({ staff }) {
  const [expandedSessions, setExpandedSessions] = useState(false);

  const getWorkingSessions = () => {
    if (!staff || !staff.working_sessions) return null;
    return staff.working_sessions;
  };

  const workingSessions = getWorkingSessions();

  if (!workingSessions || !workingSessions.sessions || workingSessions.sessions.length === 0) {
    return (
      <div style={{ marginTop: "15px", backgroundColor: "white", padding: "20px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
        <p style={{ margin: "0 0 12px 0", fontWeight: "600", color: "#374151", fontSize: "14px" }}>
          ⏱️ Working Sessions ({workingSessions?.total_sessions || 0})
        </p>
        <div style={{ 
          padding: "30px", 
          color: "#999", 
          fontSize: "13px", 
          textAlign: "center", 
          backgroundColor: "#f9fafb", 
          borderRadius: "6px",
          border: "1px dashed #e5e7eb"
        }}>
          ℹ️ No working session data available
        </div>
      </div>
    );
  }

  const sessions = workingSessions.sessions || [];

  return (
    <div style={{ marginTop: "15px", backgroundColor: "white", padding: "20px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h5 style={{ margin: "0", fontWeight: "600", color: "#374151", fontSize: "14px" }}>
          ⏱️ Working Sessions ({sessions.length} sessions)
        </h5>
        <button
          onClick={() => setExpandedSessions(!expandedSessions)}
          style={{
            padding: "6px 12px",
            backgroundColor: "#e0e7ff",
            color: "#4f46e5",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "500",
            fontSize: "12px",
            transition: "all 0.2s"
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = "#c7d2fe"}
          onMouseOut={(e) => e.target.style.backgroundColor = "#e0e7ff"}
        >
          {expandedSessions ? "Collapse" : "Expand"}
        </button>
      </div>

      <div style={{ 
        maxHeight: expandedSessions ? "600px" : "300px", 
        overflowY: "auto",
        overflowX: "hidden"
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {sessions.map((session, idx) => (
            <div 
              key={idx}
              style={{
                padding: "15px",
                backgroundColor: idx % 2 === 0 ? "#f9fafb" : "#ffffff",
                borderLeft: "4px solid #8b5cf6",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                transition: "all 0.15s"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f3e8ff";
                e.currentTarget.style.borderLeftColor = "#7c3aed";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#f9fafb" : "#ffffff";
                e.currentTarget.style.borderLeftColor = "#8b5cf6";
              }}
            >
              {/* Session Number and Duration */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: "#e9d5ff",
                  color: "#7c3aed",
                  fontSize: "13px",
                  fontWeight: "700",
                  border: "2px solid #8b5cf6"
                }}>
                  {idx + 1}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "11px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>DURATION</div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#8b5cf6" }}>
                    {session.duration_hours}h
                  </div>
                </div>
              </div>

              {/* Entry Details */}
              <div style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: "11px", fontWeight: "600", color: "#10b981", textTransform: "uppercase", marginBottom: "6px" }}>
                  ✅ Check-In
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <div style={{ fontSize: "10px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>Time</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>{session.entry}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>Camera</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#4f46e5" }}>Camera {session.entry_camera}</div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: "10px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>Location</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>📍 {session.entry_location}</div>
                  </div>
                </div>
              </div>

              {/* Exit Details */}
              <div>
                <div style={{ fontSize: "11px", fontWeight: "600", color: "#ef4444", textTransform: "uppercase", marginBottom: "6px" }}>
                  ❌ Check-Out
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <div style={{ fontSize: "10px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>Time</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>{session.exit}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>Camera</div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#f59e0b" }}>Camera {session.exit_camera}</div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: "10px", color: "#999", fontWeight: "500", marginBottom: "2px" }}>Location</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>📍 {session.exit_location}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Footer */}
      {sessions.length > 0 && (
        <div style={{ 
          marginTop: "15px", 
          paddingTop: "15px", 
          borderTop: "1px solid #e5e7eb",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "10px"
        }}>
          <div style={{ padding: "10px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "4px" }}>Total Sessions</div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#8b5cf6" }}>{sessions.length}</div>
          </div>
          <div style={{ padding: "10px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "4px" }}>Total Hours</div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#8b5cf6" }}>{workingSessions.total_hours.toFixed(1)}h</div>
          </div>
          <div style={{ padding: "10px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "4px" }}>Entries</div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#10b981" }}>{workingSessions.num_entries}</div>
          </div>
          <div style={{ padding: "10px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "4px" }}>Exits</div>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#ef4444" }}>{workingSessions.num_exits}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// DETECTION LOGS COMPONENT
// ============================================================
function DetectionLogsDisplay({ staff, totalDetections }) {
  const [expandedLog, setExpandedLog] = useState(false);

  const parseTime = (timeValue) => {
    if (!timeValue) return null;
    try {
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) return null;
      return date;
    } catch {
      return null;
    }
  };

  const formatTime = (timeValue) => {
    const date = parseTime(timeValue);
    if (!date) return "—";
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    });
  };

  const getDetectionLogs = () => {
    if (!staff) return [];
    const logArray = staff.detection_log || staff.detectionLog || staff.detection_logs || staff.detections || [];
    if (!Array.isArray(logArray)) return [];
    return logArray.filter(log => log && typeof log === 'object');
  };

  const detectionLogs = getDetectionLogs();
  const logCount = detectionLogs.length || totalDetections || 0;

  if (!detectionLogs || detectionLogs.length === 0) {
    return (
      <div style={{ marginTop: "15px", backgroundColor: "white", padding: "20px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
        <p style={{ margin: "0 0 12px 0", fontWeight: "600", color: "#374151", fontSize: "14px" }}>
          📅 Detection Timeline ({logCount} detections)
        </p>
        <div style={{ 
          padding: "30px", 
          color: "#999", 
          fontSize: "13px", 
          textAlign: "center", 
          backgroundColor: "#f9fafb", 
          borderRadius: "6px",
          border: "1px dashed #e5e7eb"
        }}>
          ℹ️ No detailed detection logs available
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "15px", backgroundColor: "white", padding: "20px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h5 style={{ margin: "0", fontWeight: "600", color: "#374151", fontSize: "14px" }}>
          📅 Detection Timeline ({detectionLogs.length} detections)
        </h5>
        <button
          onClick={() => setExpandedLog(!expandedLog)}
          style={{
            padding: "6px 12px",
            backgroundColor: "#e0e7ff",
            color: "#4f46e5",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "500",
            fontSize: "12px",
            transition: "all 0.2s"
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = "#c7d2fe"}
          onMouseOut={(e) => e.target.style.backgroundColor = "#e0e7ff"}
        >
          {expandedLog ? "Collapse" : "Expand"}
        </button>
      </div>

      <div style={{ 
        maxHeight: expandedLog ? "600px" : "300px", 
        overflowY: "auto",
        overflowX: "hidden"
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {detectionLogs.map((log, idx) => {
            const timeStr = formatTime(log.time || log.detection_time);
            const cameraName = log.camera || log.camera_number || log.camera_id || "Unknown";
            const location = log.location || log.camera_location || "N/A";
            const confidence = log.confidence;
            const detectionType = log.detection_type || "Unknown";
            
            let confidencePercent = "—";
            if (confidence !== null && confidence !== undefined && confidence !== "") {
              const confValue = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
              if (!isNaN(confValue)) {
                confidencePercent = (confValue >= 0 && confValue <= 1) 
                  ? (confValue * 100).toFixed(1)
                  : confValue.toFixed(1);
              }
            }

            const confidenceNum = parseFloat(confidencePercent);
            let confidenceColor = "#ef4444";
            let confidenceBg = "#fee2e2";
            if (!isNaN(confidenceNum)) {
              if (confidenceNum >= 90) {
                confidenceColor = "#10b981";
                confidenceBg = "#d1fae5";
              } else if (confidenceNum >= 70) {
                confidenceColor = "#f59e0b";
                confidenceBg = "#fef3c7";
              }
            }

            // Get badge color for detection type
            let typeBadgeColor = "#999";
            let typeBadgeBg = "#f3f4f6";
            if (detectionType === "Entry") {
              typeBadgeColor = "#10b981";
              typeBadgeBg = "#d1fae5";
            } else if (detectionType === "Exit") {
              typeBadgeColor = "#ef4444";
              typeBadgeBg = "#fee2e2";
            }

            return (
              <div 
                key={idx}
                style={{
                  padding: "12px 14px",
                  backgroundColor: idx % 2 === 0 ? "#f9fafb" : "#ffffff",
                  borderLeft: "3px solid #4f46e5",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  display: "grid",
                  gridTemplateColumns: "80px 1fr 110px 80px 70px",
                  gap: "12px",
                  alignItems: "center",
                  transition: "all 0.15s"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f9ff";
                  e.currentTarget.style.borderLeftColor = "#3b82f6";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#f9fafb" : "#ffffff";
                  e.currentTarget.style.borderLeftColor = "#4f46e5";
                }}
              >
                <div style={{ textAlign: "center", paddingRight: "8px", borderRight: "1px solid #e5e7eb" }}>
                  <div style={{ fontSize: "11px", color: "#999", fontWeight: "500", marginBottom: "4px", textTransform: "uppercase" }}>Time</div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>{timeStr}</div>
                </div>

                <div style={{ paddingLeft: "8px" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#4f46e5", marginBottom: "3px" }}>📷 Camera {cameraName}</div>
                  <div style={{ fontSize: "11px", color: "#666" }}>📍 {location}</div>
                  <div style={{
                    display: "inline-block",
                    fontSize: "10px",
                    fontWeight: "600",
                    color: typeBadgeColor,
                    backgroundColor: typeBadgeBg,
                    padding: "2px 6px",
                    borderRadius: "3px",
                    marginTop: "3px"
                  }}>
                    {detectionType}
                  </div>
                </div>

                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "10px", color: "#999", fontWeight: "600", marginBottom: "4px" }}>CONF</div>
                  <div style={{
                    display: "inline-block",
                    fontSize: "12px",
                    fontWeight: "700",
                    color: confidenceColor,
                    backgroundColor: confidenceBg,
                    padding: "4px 8px",
                    borderRadius: "4px"
                  }}>
                    {confidencePercent}%
                  </div>
                </div>

                <div style={{ textAlign: "center" }}>
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor: "#e0e7ff",
                    color: "#4f46e5",
                    fontSize: "12px",
                    fontWeight: "700",
                    border: "2px solid #4f46e5"
                  }}>
                    {idx + 1}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {detectionLogs.length > 0 && (
        <div style={{ 
          marginTop: "12px", 
          paddingTop: "12px", 
          borderTop: "1px solid #e5e7eb",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
          gap: "8px"
        }}>
          <div style={{ padding: "8px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "2px" }}>Total</div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>{detectionLogs.length}</div>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "2px" }}>First</div>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#4f46e5" }}>{formatTime(detectionLogs[0]?.time || detectionLogs[0]?.detection_time)}</div>
          </div>
          <div style={{ padding: "8px", backgroundColor: "#f3f4f6", borderRadius: "4px", textAlign: "center", fontSize: "11px" }}>
            <div style={{ color: "#666", fontWeight: "500", marginBottom: "2px" }}>Last</div>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#4f46e5" }}>{formatTime(detectionLogs[detectionLogs.length - 1]?.time || detectionLogs[detectionLogs.length - 1]?.detection_time)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function StaffAttendanceTable() {
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  // View state
  const [activeView, setActiveView] = useState("daily");

  // Daily view states
  const [selectedDate, setSelectedDate] = useState(formattedToday);
  const [staffMembers, setStaffMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedDetectionLog, setExpandedDetectionLog] = useState(null);

  // Date range report states
  const [rangeStartDate, setRangeStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [rangeEndDate, setRangeEndDate] = useState(formattedToday);
  const [rangeData, setRangeData] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Range report filters
  const [rangeSelectedDepartment, setRangeSelectedDepartment] = useState("");
  const [rangeSelectedLocation, setRangeSelectedLocation] = useState("");
  const [rangeSelectedStaffName, setRangeSelectedStaffName] = useState("");

  const fetchDataByDate = async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);
      const payload = {
        date: selectedDate,
        page_number: 1,
        page_size: 1000,
      };

      const response = await get_staff_summary(payload);
      const fetchedData = response?.data?.data || [];
      const summaryData = response?.data?.summary || null;

      console.log("Staff data received:", fetchedData);
      console.log("Summary:", summaryData);

      setStaffMembers(fetchedData);
      setSummary(summaryData);

      if (fetchedData.length === 0 && !summaryData) {
        alert("No records found for this date.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Failed to fetch data.");
      setStaffMembers([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchDateRangeReport = async () => {
    if (!rangeStartDate || !rangeEndDate) return;

    try {
      setRangeLoading(true);
      
      const start = new Date(rangeStartDate);
      const end = new Date(rangeEndDate);
      const allDates = [];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        allDates.push(d.toISOString().split("T")[0]);
      }

      const allData = [];
      for (const date of allDates) {
        try {
          const payload = {
            date: date,
            page_number: 1,
            page_size: 1000,
          };
          const response = await get_staff_summary(payload);
          const fetchedData = response?.data?.data || [];
          allData.push(...fetchedData);
        } catch (err) {
          console.error(`Error fetching data for ${date}:`, err);
        }
      }

      setRangeData(allData);
      if (allData.length === 0) {
        alert("No records found for the selected date range.");
      }
    } catch (error) {
      console.error("Error fetching range data:", error);
      alert("Failed to fetch date range report.");
      setRangeData([]);
    } finally {
      setRangeLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === "daily") {
      fetchDataByDate();
    }
  }, [selectedDate, activeView]);

  const filteredStaff = useMemo(() => {
    return staffMembers.filter((staff) => {
      const matchesStaffId = !selectedStaffId || staff.staff_id === selectedStaffId;
      const matchesDepartment = !selectedDepartment || staff.department === selectedDepartment;
      const matchesStatus = selectedStatus === "all" || staff.status === selectedStatus;
      const matchesLocation = !selectedLocation || staff.location === selectedLocation;

      return matchesStaffId && matchesDepartment && matchesStatus && matchesLocation;
    });
  }, [staffMembers, selectedStaffId, selectedDepartment, selectedStatus, selectedLocation]);

  const uniqueStaffIds = [...new Set(staffMembers.map((s) => s.staff_id).filter(Boolean))];
  const uniqueDepartments = [...new Set(staffMembers.map((s) => s.department).filter(Boolean))];
  const uniqueLocations = [...new Set(staffMembers.map((s) => s.location).filter(Boolean))];

  const correctedSummary = useMemo(() => {
    if (!summary) return null;

    const totalStaff = summary.total_staff || 0;
    const presentCount = summary.present || 0;
    const absentCount = Math.max(totalStaff - presentCount, 0);
    const attendancePercentage = totalStaff > 0 ? ((presentCount / totalStaff) * 100).toFixed(1) : 0;

    return {
      ...summary,
      present: presentCount,
      absent: absentCount,
      attendance_percentage: attendancePercentage,
      total_staff: totalStaff,
    };
  }, [summary]);

  const processRangeData = () => {
    const reportMap = {};

    rangeData.forEach((record) => {
      const staffId = record.staff_id;
      if (!reportMap[staffId]) {
        reportMap[staffId] = {
          staff_id: staffId,
          staff_name: record.staff_name,
          department: record.department,
          location: record.location,
          total_days: 0,
          present_days: 0,
          absent_days: 0,
          attendance_percentage: 0,
          total_working_hours: 0,
          avg_working_hours: 0,
          total_detections: 0,
        };
      }
      reportMap[staffId].total_days += 1;
      if (record.status === "Present") {
        reportMap[staffId].present_days += 1;
        reportMap[staffId].total_working_hours += record.total_working_hours || 0;
        reportMap[staffId].total_detections += record.total_detection_logs || 0;
      } else {
        reportMap[staffId].absent_days += 1;
      }
    });

    Object.values(reportMap).forEach((record) => {
      record.attendance_percentage = record.total_days > 0
        ? Math.round((record.present_days / record.total_days) * 100)
        : 0;
      record.avg_working_hours = record.present_days > 0
        ? (record.total_working_hours / record.present_days).toFixed(1)
        : 0;
    });

    let filtered = Object.values(reportMap);
    
    if (rangeSelectedDepartment) {
      filtered = filtered.filter(r => r.department === rangeSelectedDepartment);
    }
    if (rangeSelectedLocation) {
      filtered = filtered.filter(r => r.location === rangeSelectedLocation);
    }
    if (rangeSelectedStaffName) {
      filtered = filtered.filter(r => 
        r.staff_name.toLowerCase().includes(rangeSelectedStaffName.toLowerCase())
      );
    }

    return filtered.sort((a, b) => a.staff_id.localeCompare(b.staff_id));
  };

  const rangeReport = processRangeData();

  const rangeUniqueDepartments = [...new Set(rangeData.map((s) => s.department).filter(Boolean))];
  const rangeUniqueLocations = [...new Set(rangeData.map((s) => s.location).filter(Boolean))];
  const rangeUniqueStaffNames = [...new Set(rangeData.map((s) => s.staff_name).filter(Boolean))].sort();

  const handleDownloadPDF = async () => {
    try {
      const response = await download_staff_pdf({ date: selectedDate });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `staff_report_${selectedDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
            alert("✅ PDF downloaded!");

    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF.");
    }
  };

  const totalStaffInRange = rangeReport.length;
  const averageAttendance = totalStaffInRange > 0
    ? Math.round(rangeReport.reduce((sum, r) => sum + r.attendance_percentage, 0) / totalStaffInRange)
    : 0;
  const totalWorkingHours = rangeReport.reduce((sum, r) => sum + r.total_working_hours, 0);
  const avgWorkingHours = totalStaffInRange > 0
    ? (totalWorkingHours / totalStaffInRange).toFixed(1)
    : 0;

  const getAttendanceColor = (percentage) => {
    if (percentage >= 80) return "#10b981";
    if (percentage >= 60) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div style={{ padding: "20px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ margin: "0 0 16px 0", fontSize: "28px", fontWeight: "700", color: "#1a1a1a" }}>
          👥 Staff Attendance Tracking
        </h1>
        <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
          View staff working hours, detection logs, working sessions, and facial recognition data
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #e5e7eb" }}>
        <button
          onClick={() => setActiveView("daily")}
          style={{
            padding: "12px 24px",
            backgroundColor: "transparent",
            color: activeView === "daily" ? "#4f46e5" : "#6b7280",
            border: "none",
            borderBottom: activeView === "daily" ? "3px solid #4f46e5" : "none",
            cursor: "pointer",
            fontWeight: activeView === "daily" ? "600" : "500",
            fontSize: "15px",
            transition: "all 0.2s ease",
          }}
        >
          📅 Daily View
        </button>
        <button
          onClick={() => setActiveView("range")}
          style={{
            padding: "12px 24px",
            backgroundColor: "transparent",
            color: activeView === "range" ? "#4f46e5" : "#6b7280",
            border: "none",
            borderBottom: activeView === "range" ? "3px solid #4f46e5" : "none",
            cursor: "pointer",
            fontWeight: activeView === "range" ? "600" : "500",
            fontSize: "15px",
            transition: "all 0.2s ease",
          }}
        >
          📊 Date Range Report
        </button>
      </div>

      {/* Daily View Filters */}
      {activeView === "daily" && (
        <div
          style={{
            backgroundColor: "white",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                📅 Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d0d0d0",
                  fontSize: "14px",
                  backgroundColor: "white",
                }}
              />
            </div>

            <div>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                🆔 Staff ID
              </label>
              <select
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d0d0d0",
                  fontSize: "14px",
                  minWidth: "140px",
                  backgroundColor: "white",
                }}
              >
                <option value="">All Staff</option>
                {uniqueStaffIds.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                📊 Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d0d0d0",
                  fontSize: "14px",
                  minWidth: "140px",
                  backgroundColor: "white",
                }}
              >
                <option value="all">All Status</option>
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                🏢 Department
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d0d0d0",
                  fontSize: "14px",
                  minWidth: "140px",
                  backgroundColor: "white",
                }}
              >
                <option value="">All Departments</option>
                {uniqueDepartments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                📍 Location
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d0d0d0",
                  fontSize: "14px",
                  minWidth: "160px",
                  backgroundColor: "white",
                }}
              >
                <option value="">All Locations</option>
                {uniqueLocations.map((location) => (
                  <option key={location} value={location}>
                    📍 {location}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Report Controls */}
      {activeView === "range" && (
        <>
          <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", marginBottom: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>From:</label>
                <input
                  type="date"
                  value={rangeStartDate}
                  onChange={(e) => setRangeStartDate(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #d0d0d0",
                    fontSize: "14px", 
                    backgroundColor: "white",
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label style={{ fontSize: "14px", fontWeight: "500", color: "#374151" }}>To:</label>
                <input
                  type="date"
                  value={rangeEndDate}
                  onChange={(e) => setRangeEndDate(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #d0d0d0",
                    fontSize: "14px",
                    backgroundColor: "white",
                  }}
                />
              </div>

              <button
                onClick={fetchDateRangeReport}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4f46e5",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                📊 Generate Report
              </button>
            </div>
          </div>

          {rangeData.length > 0 && (
            <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", marginBottom: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                {rangeUniqueDepartments.length > 0 && (
                  <select
                    value={rangeSelectedDepartment}
                    onChange={(e) => setRangeSelectedDepartment(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #d0d0d0",
                      fontSize: "14px",
                      minWidth: "140px",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">All Departments</option>
                    {rangeUniqueDepartments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                )}

                {rangeUniqueLocations.length > 0 && (
                  <select
                    value={rangeSelectedLocation}
                    onChange={(e) => setRangeSelectedLocation(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #d0d0d0",
                      fontSize: "14px",
                      minWidth: "140px",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">All Locations</option>
                    {rangeUniqueLocations.map((location) => (
                      <option key={location} value={location}>
                        📍 {location}
                      </option>
                    ))}
                  </select>
                )}

                {rangeUniqueStaffNames.length > 0 && (
                  <select
                    value={rangeSelectedStaffName}
                    onChange={(e) => setRangeSelectedStaffName(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #d0d0d0",
                      fontSize: "14px",
                      minWidth: "160px",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">All Staff Names</option>
                    {rangeUniqueStaffNames.map((name) => (
                      <option key={name} value={name}>
                        👤 {name}
                      </option>
                    ))}
                  </select>
                )}

                <button
                  onClick={() => {
                    setRangeSelectedDepartment("");
                    setRangeSelectedLocation("");
                    setRangeSelectedStaffName("");
                  }}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  ↺ Reset
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Daily View Summary Stats Cards */}
      {activeView === "daily" && !loading && correctedSummary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #4f46e5" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>TOTAL STAFF</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#1a1a1a" }}>{correctedSummary.total_staff}</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>Registered</div>
          </div>

          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #10b981" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>PRESENT TODAY</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#10b981" }}>{correctedSummary.present}</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>
              {correctedSummary.total_staff > 0 ? ((correctedSummary.present / correctedSummary.total_staff) * 100).toFixed(1) : 0}%
            </div>
          </div>

          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #ef4444" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>ABSENT TODAY</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#ef4444" }}>{correctedSummary.absent}</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>Out of {correctedSummary.total_staff}</div>
          </div>

          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #f59e0b" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>ATTENDANCE RATE</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#f59e0b" }}>{correctedSummary.attendance_percentage}%</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>Success rate</div>
          </div>
        </div>
      )}

      {/* Range Report Summary Stats Cards */}
      {activeView === "range" && !rangeLoading && rangeReport.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #4f46e5" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>TOTAL STAFF</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#1a1a1a" }}>{totalStaffInRange}</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>In range</div>
          </div>

          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #10b981" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>AVG ATTENDANCE</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#10b981" }}>{averageAttendance}%</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>Across range</div>
          </div>

          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #8b5cf6" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>TOTAL HOURS</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#8b5cf6" }}>{totalWorkingHours.toFixed(0)}h</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>All staff</div>
          </div>

          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #f59e0b" }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "500", marginBottom: "8px" }}>AVG DAILY HOURS</div>
            <div style={{ fontSize: "36px", fontWeight: "700", color: "#f59e0b" }}>{avgWorkingHours}h</div>
            <div style={{ fontSize: "11px", color: "#999", marginTop: "6px" }}>Per staff</div>
          </div>
        </div>
      )}

      {/* Daily View - Attendance Records Table */}
      {activeView === "daily" && !loading && staffMembers.length > 0 && (
        <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: "20px" }}>
          <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
            <h2 style={{ margin: "0", fontSize: "16px", fontWeight: "600", color: "#1a1a1a" }}>
              📋 Staff Records ({filteredStaff.length} of {staffMembers.length})
            </h2>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Staff ID</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Name</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Department</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>📍 Location</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Check-In</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Check-Out</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>⏱️ Working Hours</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>📊 Detection Logs</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>📷 Unique Cameras</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Status</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Confidence</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((staff, idx) => (
                  <React.Fragment key={staff.id || staff.staff_id}>
                    <tr
                      style={{
                        borderBottom: "1px solid #e5e7eb",
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                        opacity: staff.status === "Absent" ? 0.6 : 1,
                      }}
                    >
                      <td style={{ padding: "12px 16px", fontWeight: "600", color: "#1a1a1a" }}>
                        {staff.staff_id}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#1a1a1a" }}>
                        {staff.staff_name}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#666" }}>
                        {staff.department || "—"}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#666", fontWeight: "500" }}>
                        {staff.location ? `📍 ${staff.location}` : "—"}
                      </td>
                      <td style={{ padding: "12px 16px", color: staff.check_in_time ? "#10b981" : "#999", fontWeight: "500" }}>
                        {staff.check_in_time ? staff.check_in_time.substring(0, 8) : "—"}
                      </td>
                      <td style={{ padding: "12px 16px", color: staff.check_out_time ? "#ef4444" : "#999", fontWeight: "500" }}>
                        {staff.check_out_time ? staff.check_out_time.substring(0, 8) : "—"}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center", color: "#1a1a1a", fontWeight: "600" }}>
                        {staff.total_working_hours || 0} h
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center", color: "#4f46e5", fontWeight: "600" }}>
                        {staff.total_detection_logs || 0}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center", color: "#f59e0b", fontWeight: "600" }}>
                        {staff.unique_camera_count || 0}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: staff.status === "Present" ? "#d1fae5" : "#fee2e2",
                            color: staff.status === "Present" ? "#065f46" : "#991b1b",
                          }}
                        >
                          {staff.status === "Present" ? "✅ Present" : "❌ Absent"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center", color: "#666" }}>
                        {staff.confidence ? `${(staff.confidence * 100).toFixed(1)}%` : "—"}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <button
                          onClick={() => {
                            const staffKey = staff.id || staff.staff_id;
                            setExpandedDetectionLog(expandedDetectionLog === staffKey ? null : staffKey);
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#4f46e5",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                            transition: "background-color 0.2s",
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = "#4338ca"}
                          onMouseOut={(e) => e.target.style.backgroundColor = "#4f46e5"}
                        >
                          {expandedDetectionLog === (staff.id || staff.staff_id) ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Row - Detection Details with Images */}
                    {expandedDetectionLog === (staff.id || staff.staff_id) && (
                      <tr style={{ backgroundColor: "#f0f9ff", borderBottom: "1px solid #e5e7eb" }}>
                        <td colSpan="12" style={{ padding: "20px" }}>
                          <div>
                            <h4 style={{ margin: "0 0 20px 0", color: "#1a1a1a", fontSize: "16px", fontWeight: "700" }}>
                              🔍 Detailed Analysis - {staff.staff_id}
                            </h4>

{/* Facial Recognition Images */}
<div style={{ marginBottom: "20px", backgroundColor: "white", padding: "15px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
  <h5 style={{ margin: "0 0 15px 0", color: "#374151", fontSize: "14px", fontWeight: "600" }}>
    📸 Facial Recognition
  </h5>
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px" }}>
    
    {/* Original Face */}
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: "8px", fontWeight: "600", color: "#374151", fontSize: "12px" }}>
        Original Face
      </div>
      {staff.original_face ? (
        <AsyncImage
          src={`${REACT_APP_API_URL}/${API_ROUTES.get_staff_original_image(staff.id)}`}
          alt="Original Face"
          style={{
            width: "100%",
            maxWidth: "130px",
            height: "130px",
            borderRadius: "6px",
            border: "2px solid #10b981",
            objectFit: "cover",
          }}
        />
      ) : (
        <div style={{ width: "100%", maxWidth: "130px", height: "130px", borderRadius: "6px", backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: "11px", border: "2px solid #e5e7eb" }}>
          No image
        </div>
      )}
    </div>

    {/* Detected Face */}
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: "8px", fontWeight: "600", color: "#374151", fontSize: "12px" }}>
        Detected Face
      </div>
      {staff.detected_face ? (
        <AsyncImage
          src={`${REACT_APP_API_URL}/${API_ROUTES.get_staff_detected_image(staff.id)}`}
          alt="Detected Face"
          style={{
            width: "100%",
            maxWidth: "130px",
            height: "130px",
            borderRadius: "6px",
            border: "2px solid #4f46e5",
            objectFit: "cover",
          }}
        />
      ) : (
        <div style={{ width: "100%", maxWidth: "130px", height: "130px", borderRadius: "6px", backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: "11px", border: "2px solid #e5e7eb" }}>
          No image
        </div>
      )}
    </div>

  </div>
</div>

                            {/* Working Sessions Section */}
                            <WorkingSessionsDisplay staff={staff} />

                            {/* Detection Logs Section */}
                            <DetectionLogsDisplay staff={staff} totalDetections={staff.total_detection_logs} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Range Report Table */}
      {activeView === "range" && !rangeLoading && rangeReport.length > 0 && (
        <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: "20px" }}>
          <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
            <h2 style={{ margin: "0", fontSize: "16px", fontWeight: "600", color: "#1a1a1a" }}>
              📋 Staff Analysis Report ({rangeReport.length} staff members)
            </h2>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Staff ID</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Name</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Department</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Total Days</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Present</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Absent</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Attendance %</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Total Hours</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Avg Daily Hours</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#374151" }}>Total Detections</th>
                </tr>
              </thead>
              <tbody>
                {rangeReport.map((record, idx) => (
                  <tr key={record.staff_id} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                    <td style={{ padding: "12px 16px", fontSize: "14px", fontWeight: "600", color: "#1a1a1a" }}>
                      {record.staff_id}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", color: "#1a1a1a" }}>
                      {record.staff_name}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666" }}>
                      {record.department || "—"}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666", textAlign: "center", fontWeight: "600" }}>
                      {record.total_days}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "600",
                        backgroundColor: "#d1fae5",
                        color: "#065f46",
                      }}>
                        {record.present_days}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "600",
                        backgroundColor: "#fee2e2",
                        color: "#991b1b",
                      }}>
                        {record.absent_days}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", textAlign: "center" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        fontWeight: "600",
                        backgroundColor: getAttendanceColor(record.attendance_percentage) === "#10b981" ? "#d1fae5" : (getAttendanceColor(record.attendance_percentage) === "#f59e0b" ? "#fcd34d" : "#fee2e2"),
                        color: getAttendanceColor(record.attendance_percentage) === "#10b981" ? "#065f46" : (getAttendanceColor(record.attendance_percentage) === "#f59e0b" ? "#92400e" : "#991b1b"),
                      }}>
                        {record.attendance_percentage}%
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666", textAlign: "center", fontWeight: "600" }}>
                      {record.total_working_hours.toFixed(1)}h
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", color: "#666", textAlign: "center", fontWeight: "600" }}>
                      {record.avg_working_hours}h
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "14px", color: "#4f46e5", textAlign: "center", fontWeight: "600" }}>
                      {record.total_detections}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading */}
      {(loading || rangeLoading) && (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          ⏳ Loading...
        </div>
      )}

      {/* Empty State for Daily */}
      {activeView === "daily" && !loading && staffMembers.length === 0 && (
        <div style={{ backgroundColor: "white", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#999" }}>
          No records found for {selectedDate}.
        </div>
      )}

      {/* Empty State for Range */}
      {activeView === "range" && !rangeLoading && rangeData.length === 0 && (
        <div style={{ backgroundColor: "white", padding: "40px", borderRadius: "8px", textAlign: "center", color: "#999" }}>
          No records found. Click "Generate Report" to fetch staff attendance data for the selected date range.
        </div>
      )}

      {/* Download Button - Daily View */}
      {activeView === "daily" && !loading && staffMembers.length > 0 && (
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleDownloadPDF}
            style={{
              padding: "12px 24px",
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "14px",
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = "#059669"}
            onMouseOut={(e) => e.target.style.backgroundColor = "#10b981"}
          >
            📄 Download PDF 
          </button>
        </div>
      )}
    </div>
  );
}
